import './loanDocumentPage.scss';

import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import heroMeditation from '../../../assets/hero-meditation.svg';
import logo from '../../../assets/logo-default.svg';
import { NavigationPane } from '../../../components/shared_new/NavigationPane/NavigationPane';
import { FeatureFlag } from '../../../store/featureStore/enums/FeatureFlag';
import { useFeatureFlag } from '../../../store/featureStore/hooks/useFeatureFlag';
import useNewLoanDocumentStore from '../../../store/loanDocumentStore/useNewLoanDocumentStore';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useUserStore from '../../../store/userStore/useUserStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { setDrawerOpen } = useNavigationPaneStore();

  const {
    computed: { userFirstName },
  } = useUserStore();

  const { handleSubmit } = useForm();

  const isSimplifiedWorkflowEnabled = useFeatureFlag(
    FeatureFlag.ENABLE_SIMPLIFIED_LOAN_DOCUMENTS_WORKFLOW,
  );

  const {
    computed: { hasProvidedDocuments },
    isStartingWorkflow,
  } = useNewLoanDocumentStore();

  const formSubmit = (): Promise<void> => {
    if (isSimplifiedWorkflowEnabled) {
      navigate('/loan-documents/document-upload');
      return;
    }

    navigate('/loan-documents/list');
  };

  return (
    <div className="loan-document-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          <header data-testid={locator.pages.loanDocument.header.root}>
            <img alt={t('imageAlts.logo')} src={logo} />
            <button
              aria-label="Toggle drawer"
              data-testid={locator.pages.loanDocument.header.drawerButton}
              type="button"
              onClick={() => {
                setDrawerOpen(true);
              }}
            >
              <MenuRoundedIcon />
            </button>
          </header>

          <div className="progress-container" role="complementary">
            <LinearProgress
              aria-label="Loan documents completed percentage"
              data-testid={locator.pages.loanDocument.progress.root}
              value={15}
              variant="determinate"
            />
          </div>

          <main>
            <figure>
              <img alt={t('imageAlts.heroMeditation')} src={heroMeditation} />
            </figure>
            <form
              data-testid={locator.pages.loanDocument.form.root}
              noValidate
              onSubmit={handleSubmit(formSubmit)}
            >
              <div className="form-content">
                <h1 data-testid={locator.pages.loanDocument.form.h1}>
                  {t('LoanDocumentPage.title', { firstName: userFirstName })}
                </h1>
                <h2 data-testid={locator.pages.loanDocument.form.h2}>
                  {t('LoanDocumentPage.subtitle')}
                </h2>

                <div style={{ marginBottom: '3rem' }}>
                  <LoadingButton
                    color="primary"
                    data-testid={locator.pages.loanDocument.form.submitButton}
                    loading={isStartingWorkflow}
                    startIcon={<></>}
                    type="submit"
                    variant="contained"
                    fullWidth
                  >
                    {hasProvidedDocuments
                      ? t('LoanDocumentPage.resume')
                      : t('LoanDocumentPage.continue')}
                  </LoadingButton>
                </div>
              </div>
              <footer data-testid={locator.pages.loanDocument.footer.root}>
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>

        <NavigationPane withDrawer withUserNameHeader />
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'LoanDocumentPage';
